"use client";

import * as stylex from "@stylexjs/stylex";
import { Upload } from "lucide-react";
import { useEffect, useRef } from "react";
import { copy } from "@/copy";
import type { PhotoVariant } from "@/data/repos";
import type { Photo } from "@/domain/schemas";
import "@/theme/tokens.stylex";
import { colors, motion, radii, space } from "@/theme/tokens.stylex";
import "@/theme/type.stylex";
import { type } from "@/theme/type.stylex";
import { Button } from "@/ui/Button";
import { Eyebrow } from "@/ui/Eyebrow";
import { revealDelta, THUMB_REVEAL_PX } from "../layout";
import { photoDate } from "../slideshow";
import type { UploadSummary, UploadTile } from "../uploads";
import { AddFromPhonePanel, type QrShown } from "./AddFromPhone";
import { PendingUpload } from "./PendingUpload";
import { UploadRow } from "./UploadRow";
type PhotoPanelProps = {
  photos: readonly Photo[];
  thisYear: number;
  updated?: string;
  selected: number;
  uploads: readonly UploadTile[];
  summary: UploadSummary;
  online: boolean;
  qr?: QrShown;
  columns: stylex.StyleXStyles;
  frame: stylex.StyleXStyles;
  photoSrc: (photoId: string, variant: PhotoVariant) => string;
  onSelect: (index: number) => void;
  onUpload: () => void;
  onChooseFiles: () => void;
  onCloseQr: () => void;
  onRetry: (id: string) => void;
  onRetryAll: () => void;
  onRemove: (id: string) => void;
  onClear: () => void;
};
const styles = {
  root: {
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kOIVth: "x1hu4l6k",
    k7Eaqz: "xeuugli",
    kB7OPa: "x9f619",
    kWkggS: "xh540kj",
    kaIpWk: "xwe7p7i",
    kLKAdn: "x1q88djf",
    kg3NbH: "xw3ndq7",
    kGO01o: "x2xw44r",
    $$css: true
  },
  grid: {
    k1xSpc: "xrvj5dj",
    kfiyM8: "x10ukxgv",
    kOIVth: "xddi9bj",
    kogj98: "x1ghz6dp",
    kmVPX3: "x1717udv",
    kB88ic: "xe8uvvx",
    $$css: true
  }
};
type ThumbProps = {
  photo: Photo;
  thisYear: number;
  src: string;
  selected: boolean;
  onSelect: () => void;
};
export function PhotoPanel({
  photos,
  thisYear,
  selected,
  uploads,
  summary,
  online,
  qr,
  columns,
  frame,
  photoSrc,
  onSelect,
  onUpload,
  onChooseFiles,
  onCloseQr,
  onRetry,
  onRetryAll,
  onRemove,
  onClear,
  updated
}: PhotoPanelProps) {
  const scroller = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (selected >= 0) revealSelected(scroller.current);
  }, [selected]);
  return <div {...stylex.props(styles.root, frame)}>
      <div className="x78zum5 x1qughib x6s0dn4 x152posx x1gg8mnh x1szssqx">
        <span className="xeuugli xuxw1ft xb3r6kr xlyipyv">
          <Eyebrow>
            {updated === undefined ? copy.photos.count(photos.length) : copy.zone.updated(copy.photos.count(photos.length), updated)}
          </Eyebrow>
        </span>
        <Button variant="ghost" icon={Upload} disabled={!online} onClick={onUpload}>
          {copy.photos.upload}
        </Button>
      </div>
      {qr && <AddFromPhonePanel qr={qr} onChooseFiles={onChooseFiles} onDone={onCloseQr} />}
      {summary.kind !== "none" && <UploadRow summary={summary} onRetryAll={onRetryAll} onClear={onClear} />}
      <div ref={scroller} className="x1iyjqo2 xs83m0k xdl72j9 x2lwn1j x1odjw0f xish69e x1x57ub9 x1iorvi4 xdqyycr xwib8y2">
        <ul aria-label={copy.photos.list} {...stylex.props(styles.grid, columns)}>
          {uploads.map(tile => <li key={tile.id} className="xeuugli">
              <PendingUpload tile={tile} onRetry={onRetry} onRemove={onRemove} />
            </li>)}
          {photos.map((photo, index) => <li key={photo.id} className="xeuugli">
              <Thumb photo={photo} thisYear={thisYear} src={photoSrc(photo.id, "thumb")} selected={index === selected} onSelect={() => onSelect(index)} />
            </li>)}
        </ul>
      </div>
    </div>;
}
function Thumb({
  photo,
  thisYear,
  src,
  selected,
  onSelect
}: ThumbProps) {
  return <button type="button" aria-label={copy.photos.show(photo.title)} aria-pressed={selected} onClick={onSelect} className="xc342km xjbqb8w x1717udv xjb2p0i x1heor9g xdpxx8g x1ypdohk x78zum5 xdt5ytf x152posx xh8yej3 xeuugli x11xpdln x1bpocu2 x1em5f5i xd3so5o">
      {/* biome-ignore lint/performance/noImgElement: photos come pre-sized from the authenticated file route and the service worker cache, not the Next image optimizer */}
      <img src={src} alt="" {...{
      0: {
        className: "xh8yej3 x1dltgaz xl1xv1r x1fv5t7x x1lliihq"
      },
      1: {
        className: "xh8yej3 x1dltgaz xl1xv1r x1fv5t7x x1lliihq x3i0a7z"
      }
    }[!!selected << 0]} />
      <span className="x78zum5 xdt5ytf x195vfkc xh8yej3 xeuugli">
        <span className="x1jvydc1 xfbjfs5 xuxw1ft xb3r6kr xlyipyv">{photo.title}</span>
        <span className="xr0iwhn xhunfcg xkv8mnm xtvhhri xuxw1ft">{photoDate(photo, thisYear)}</span>
      </span>
    </button>;
}
function revealSelected(scroller: HTMLElement | null) {
  const thumb = scroller?.querySelector("[aria-pressed='true']");
  if (!scroller || !thumb || scroller.scrollHeight <= scroller.clientHeight) return;
  const delta = revealDelta(scroller.getBoundingClientRect(), thumb.getBoundingClientRect(), THUMB_REVEAL_PX);
  if (delta !== 0) scroller.scrollTop += delta;
}